import { ArrowUpRight } from "lucide-react";
import Reveal from "./Reveal";
import { scrollToId } from "@/lib/scroll";

const SERVICES = [
    {
        num: "01",
        title: "Administration & Office Support",
        desc: "Day-to-day administrative cover — filing, correspondence, data organisation and everything that keeps an office moving.",
    },
    {
        num: "02",
        title: "Data Entry & Document Management",
        desc: "Accurate, structured data entry and document systems where everything has a place and can actually be found.",
    },
    {
        num: "03",
        title: "Bookkeeping & Invoicing Assistance",
        desc: "Invoices, expenses and reconciliations prepared on time, working alongside your accountant or software.",
    },
    {
        num: "04",
        title: "Scheduling & Diary Management",
        desc: "Calendars, meetings, reminders and travel arranged quietly in the background so nothing slips.",
    },
    {
        num: "05",
        title: "Customer Correspondence",
        desc: "Professional emails, calls and follow-ups handled promptly and in your company's voice.",
    },
    {
        num: "06",
        title: "Process & Systems Organisation",
        desc: "Workflows documented and automated with Office 365, Power Apps and Flows — so your operation no longer depends on memory.",
    },
    {
        num: "07",
        title: "Systems & Data Migration",
        desc: "Hands-on experience with Sage 50, Sage Intacct and BigChange — including moving data between them accurately and without downtime.",
    },
];

const Services = () => (
    <section id="services" data-testid="services-section" className="relative py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <Reveal>
                <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-amber-400">
                    01 — What we do
                </p>
                <h2 className="mt-6 max-w-2xl font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
                    Back-office support, handled end to end.
                </h2>
            </Reveal>

            <div className="mt-16 border-t border-white/10">
                {SERVICES.map((s, i) => (
                    <Reveal key={s.num} delay={i * 0.05}>
                        <button
                            data-testid={`service-card-${s.num}`}
                            onClick={() => scrollToId("#contact")}
                            className="group grid w-full grid-cols-[auto_1fr_auto] items-center gap-6 border-b border-white/10 py-8 text-left transition-colors duration-500 hover:bg-white/[0.02] sm:gap-10 lg:py-10"
                        >
                            <span className="font-mono text-sm text-slate-500 transition-colors duration-500 group-hover:text-amber-400 sm:text-base">
                                {s.num}
                            </span>
                            <span className="min-w-0">
                                <span className="block font-display text-xl font-bold tracking-tight text-white transition-transform duration-500 group-hover:translate-x-2 sm:text-2xl lg:text-3xl">
                                    {s.title}
                                </span>
                                <span className="mt-2 block max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">
                                    {s.desc}
                                </span>
                            </span>
                            <span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 transition-all duration-500 group-hover:border-amber-400 group-hover:bg-amber-400">
                                <ArrowUpRight className="h-4 w-4 text-slate-400 transition-all duration-500 group-hover:rotate-45 group-hover:text-navy" />
                            </span>
                        </button>
                    </Reveal>
                ))}
            </div>

            <Reveal delay={0.1}>
                <div
                    data-testid="systems-strip"
                    className="glass-card mt-14 flex flex-col items-start gap-6 rounded-2xl p-8 sm:flex-row sm:items-center sm:justify-between"
                >
                    <p className="max-w-xs font-mono text-[11px] uppercase leading-relaxed tracking-[0.22em] text-slate-400">
                        Fluent in the platforms that run your back office
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                        {["Sage 50", "Sage Intacct", "BigChange", "Office 365", "Power Apps & Flows"].map((s) => (
                            <span
                                key={s}
                                data-testid={`system-chip-${s.toLowerCase().replace(/\s+/g, "-")}`}
                                className="flex items-center gap-2 rounded-full border border-electric/30 bg-electric/10 px-5 py-2.5 text-sm font-bold text-blue-200"
                            >
                                <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                                {s}
                            </span>
                        ))}
                    </div>
                </div>
            </Reveal>
        </div>
    </section>
);

export default Services;
